import { memo } from "react";
import { useEffect, useMemo, useState } from "react";
import { ShoppingCart, Filter, Package, Loader2, Snowflake, Sun, Repeat, X, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { listTires, fileUrl } from "@/lib/api";
import { useCart, fmtFt } from "@/lib/cart";
import { useShopFilter, setShopFilter } from "@/lib/shopFilter";

const SEASONS = [
  { id: "all", label: "Összes", icon: Package },
  { id: "nyari", label: "Nyári", icon: Sun },
  { id: "teli", label: "Téli", icon: Snowflake },
  { id: "negyevszakos", label: "Négyévszakos", icon: Repeat },
];

const SEASON_LABEL = { nyari: "Nyári", teli: "Téli", negyevszakos: "Négyévszakos" };

const TireCard = memo(function TireCard({ tire, index }) {
  const { add } = useCart();
  const images = tire.images || [];
  const [imgIdx, setImgIdx] = useState(0);
  const currentImg = images[imgIdx] ? fileUrl(images[imgIdx]) : null;

  const nextImg = () => setImgIdx((p) => (p + 1) % images.length);
  const prevImg = () => setImgIdx((p) => (p - 1 + images.length) % images.length);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.4), ease: [0.16, 1, 0.3, 1] }}
      data-testid={`tire-card-${tire.id}`}
      className="group relative bg-gray-100 border border-gray-200 hover:border-violet-400/50 transition-all duration-300 overflow-hidden rounded-sm"
    >
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        {currentImg ? (
          <img
            src={currentImg}
            alt={`${tire.brand} ${tire.size}`}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => { e.currentTarget.style.opacity = "0.1"; }}
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-gray-300">
            <Package className="w-16 h-16" />
          </div>
        )}

        {images.length > 1 && (
          <>
            <button
              data-testid={`tire-img-prev-${tire.id}`}
              onClick={prevImg}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center bg-gray-100/60 backdrop-blur-sm border border-gray-300 rounded-full text-gray-900 hover:bg-gray-100/80 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid={`tire-img-next-${tire.id}`}
              onClick={nextImg}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center bg-gray-100/60 backdrop-blur-sm border border-gray-300 rounded-full text-gray-900 hover:bg-gray-100/80 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {images.map((_, i) => (
                <button
                  key={i}
                  data-testid={`tire-img-dot-${tire.id}-${i}`}
                  onClick={() => setImgIdx(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === imgIdx ? "bg-gray-100" : "bg-gray-100/30"}`}
                />
              ))}
            </div>
          </>
        )}

        <div className="absolute top-3 left-3 flex gap-2">
          <span className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold bg-gray-100/70 backdrop-blur-md border border-gray-200 rounded-sm text-violet-600">
            {SEASON_LABEL[tire.season] || tire.season}
          </span>
          {tire.quantity > 0 && (
            <span className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold bg-gray-100/70 backdrop-blur-md border border-gray-200 rounded-sm text-gray-600">
              {tire.quantity} db
            </span>
          )}
        </div>
        {tire.sold_out && (
          <div className="absolute inset-0 bg-gray-100/70 grid place-items-center">
            <span className="font-display font-black text-xl text-gray-900 uppercase tracking-widest">Elkelt</span>
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-mono mb-1">{tire.brand}</div>
        <h3 className="font-display text-lg font-bold text-gray-900 leading-tight">{tire.size}</h3>

        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-gray-500">
          {tire.tread_mm != null && (
            <div className="flex items-center justify-between px-2 py-1 bg-gray-50 border border-gray-200 rounded-sm">
              <span>Profil</span>
              <span className="text-gray-900 font-bold">{tire.tread_mm} mm</span>
            </div>
          )}
          {tire.dot && (
            <div className="flex items-center justify-between px-2 py-1 bg-gray-50 border border-gray-200 rounded-sm">
              <span>DOT</span>
              <span className="text-gray-900 font-bold">{tire.dot}</span>
            </div>
          )}
        </div>

        {tire.description && (
          <p className="mt-3 text-xs text-gray-400 line-clamp-2">{tire.description}</p>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          <div>
            <div className="font-display font-black text-xl text-gray-900">{fmtFt(tire.price)}</div>
            <div className="text-[10px] text-gray-400 uppercase tracking-wider">/ darab</div>
          </div>
          <button
            data-testid={`tire-add-${tire.id}`}
            onClick={() => add(tire)}
            disabled={tire.sold_out}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-bold uppercase tracking-wider text-xs rounded-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ShoppingCart className="w-3.5 h-3.5" />
            Kosárba
          </button>
        </div>
      </div>
    </motion.div>
  );
});

export default function Shop() {
  const [tires, setTires] = useState([]);
  const [loading, setLoading] = useState(true);
  const filter = useShopFilter();
  const season = filter.season || "all";
  const sizeQuery = (filter.size || "").trim();

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const params = season !== "all" ? { season } : {};
        const data = await listTires(params);
        if (active) setTires(data);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [season]);

  const visible = useMemo(() => {
    let filtered = tires;
    if (sizeQuery) {
      const q = sizeQuery.toLowerCase().replace(/\s+/g, "");
      filtered = filtered.filter((t) => {
        const norm = (t.size || "").toLowerCase().replace(/\s+/g, "");
        // "205/" csak a szélességre illeszkedjen ("205/55 R16" igen, "1205/55 R16" nem)
        if (q.endsWith("/")) return norm.startsWith(q);
        return norm.includes(q);
      });
    }
    // Hide sold_out tires
    return filtered.filter((t) => !t.sold_out && t.quantity > 0);
  }, [tires, sizeQuery]);

  const showAll = sizeQuery || season !== "all";
  const displayTires = useMemo(() => {
    if (showAll) return visible;
    return visible.slice(0, 3);
  }, [visible, showAll]);

  const clearSize = () => setShopFilter({ size: "" });
  const setSeason = (s) => setShopFilter({ season: s });

  return (
    <section id="webshop" data-testid="shop-section" className="relative py-24 md:py-32 bg-[#F0F1F5]">
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-10"
        >
          <div className="text-xs uppercase tracking-[0.3em] text-violet-600 font-bold mb-4">
            [ 03 ] · Használt gumi webshop
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95]">
              Használt<br />
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">gumik raktárról.</span>
            </h2>
            <p className="text-gray-500 max-w-md text-base leading-relaxed">
              Alig használt, DOT-ellenőrzött, profilmélység szerint szelektált gumik. Rendeld online, szereljük a helyszínen.
            </p>
          </div>
        </motion.div>

        {/* Active filter chips */}
        {(sizeQuery || season !== "all") && (
          <div data-testid="shop-active-filters" className="mb-4 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Szűrők:</span>
            {sizeQuery && (
              <button
                type="button"
                onClick={clearSize}
                className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-mono bg-violet-500/15 border border-violet-500/40 text-violet-600 rounded-sm hover:bg-violet-500/25"
                data-testid="shop-clear-size"
              >
                Méret: <span className="text-gray-900 font-bold">{sizeQuery}</span>
                <X className="w-3 h-3" />
              </button>
            )}
            {season !== "all" && (
              <button
                type="button"
                onClick={() => setSeason("all")}
                className="inline-flex items-center gap-1.5 px-3 py-1 text-xs bg-violet-500/15 border border-violet-500/40 text-violet-600 rounded-sm hover:bg-violet-500/25"
                data-testid="shop-clear-season"
              >
                Évszak: <span className="text-gray-900 font-bold">{SEASONS.find((s) => s.id === season)?.label}</span>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="mb-8 flex items-center gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-2">
          <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {SEASONS.map((s) => {
            const Icon = s.icon;
            const active = season === s.id;
            return (
              <button
                key={s.id}
                data-testid={`shop-filter-${s.id}`}
                onClick={() => setSeason(s.id)}
                className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border transition-all ${
                  active
                    ? "bg-gradient-to-r from-blue-600 to-violet-600 border-violet-400 text-white glow-blue"
                    : "bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-900"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {s.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center">
            <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
          </div>
        ) : visible.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-gray-200 rounded-sm" data-testid="shop-empty">
            <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400">
              {sizeQuery
                ? `Nincs találat a "${sizeQuery}" méretre${season !== "all" ? ` (${SEASONS.find((s) => s.id === season)?.label})` : ""}.`
                : "Nincs megjeleníthető gumi ebben a kategóriában."}
            </p>
            <p className="text-gray-300 text-sm mt-2">Hívj minket az aktuális készletért: <a href="tel:+36305114205" className="text-violet-600 underline">+36 30 511 4205</a></p>
          </div>
        ) : (
          <>
            <div className="mb-3 text-xs text-gray-400" data-testid="shop-count">
              <span className="text-gray-900 font-bold">{visible.length}</span> gumi {sizeQuery ? `a "${sizeQuery}" méretre` : ""}
              {!showAll && visible.length > 3 && (
                <span className="text-gray-300"> — mutatjuk az első 3-at, kereséssel láthatod az összeset</span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {displayTires.map((t, i) => <TireCard key={t.id} tire={t} index={i} />)}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
